import _ from 'underscore';

import {MinimalFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as SourceConstants from 'constants/SourceConstants';
import {FileSelectorFolderFilter} from 'file-browser/FileSelectorConstants';
import {FileSummary} from 'folder-items/FileConstants';
import * as RegistryUtil from 'registry/RegistryUtil';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';
import {apiIdToIconName} from 'util/APIUtil';

import FileBrowserUtil from './FileBrowserUtil';

/**
 * Extracts the (server) parameters for FolderDAO.fetchAll from a `folderFilter`.
 */
export function folderFilterToListParams(
  folderFilter: FileSelectorFolderFilter
): Pick<FileSelectorFolderFilter, 'level'> {
  return {level: folderFilter.level};
}

/**
 * Performs client (post-server) filtering of folders, see `FileSelectorConstants.folderFilter`
 * @return {Function} - a function that can be used in `folders.filter`
 */
export function folderPredicate(folderFilter: FileSelectorFolderFilter): (folder: MinimalFolder) => boolean {
  const shouldShowFolderType = FileBrowserUtil.propsToFolderPredicate(folderFilter);
  return (folder) => {
    if (
      !folderFilter.includeHiddenProjects &&
      folder.source &&
      (folder.source.hidden || folder.source.archived || folder.source.archiveRecord)
    ) {
      return false;
    }
    if (!shouldShowFolderType(folder)) {
      return false;
    }
    if (
      !folderFilter.showRegistries &&
      folder.source &&
      folder.source.sourceType === SourceConstants.REGISTRY_TYPE
    ) {
      return false;
    }
    return true;
  };
}

function getFolderIcon(folder: MinimalFolder): BenchlingIconName {
  if (RegistryUtil.isRegistry(folder.source)) {
    return ModelTypeConstants.REGISTRY;
  }
  if (!folder.parent) {
    return ModelTypeConstants.SOURCE;
  }
  return ModelTypeConstants.FOLDER;
}

export function foldersToSelectTreeItems(folders: Array<MinimalFolder>): Array<TreeItem> {
  const folderMap = _.indexBy(folders, 'api_identifier');
  // oxlint-disable-next-line array-callback-return
  return folders.map((folder) => {
    const folderPath = [folder.name];
    let parentId = folder.parent ? folder.parent.api_identifier : null;
    let currentFolder: MinimalFolder = folder;
    while (parentId && !(parentId in folderMap)) {
      currentFolder = currentFolder.parent!;
      folderPath.unshift(currentFolder.name);
      parentId = currentFolder.parent ? currentFolder.parent.api_identifier : null;
    }
    // If we are showing the root folder, include the owner
    const user = !parentId ? currentFolder.owner_handle : undefined;
    const isStarred = folder.source && folder.source.starred && !folder.parent;
    return {
      id: folder.api_identifier,
      forceToTop: RegistryUtil.isRegistry(folder.source) || isStarred,
      icon: isStarred ? 'star' : getFolderIcon(folder),
      name: folderPath.join(' / '),
      parentId,
      user,
    };
  });
}

export function fileToSelectTreeItem(fileSummary: FileSummary): TreeItem {
  return {
    id: fileSummary.id,
    icon: apiIdToIconName(fileSummary.id),
    name: fileSummary.name,
    parentId: fileSummary.folder.api_identifier,
    user: undefined,
  };
}

export function registriesToSelectTreeItems(registries: Array<MinimalFolder>): Array<TreeItem> {
  return registries.map((registry) => ({
    id: RegistryUtil.generateFolderValue(registry.api_identifier, registry.source && registry.source.id, ''),
    icon: ModelTypeConstants.REGISTRY,
    name: registry.name,
    parentId: null,
    user: registry.owner_handle,
  }));
}
